import { orderFormSchema } from '@common/order/orderSchema';
import { collectStopChronologyViolationsFor } from '@common/order/stopChronology';
import { ToastType } from '@components';
import { LTLReferenceNumberTypes } from '@store/ltl';
import { ltlReferenceNumberStore } from '@store/ltl/store';
import {
  orderStore,
  updateOrderFormErrorPerSectionState,
  updateOrderFormErrorState,
  updateOrderFormMappedErrorSection,
} from '@store/orders';
import { handleToast, printLog } from '@utils/utils';
import { ValidationError } from 'yup';

export function validateOrder() {
  if (orderStore.order.loads.length === 0) {
    handleToast(ToastType.Error, 'Please add at least one Carrier');
    return;
  }

  try {
    orderFormSchema.validateSync(orderStore.order, {
      abortEarly: false,
    });
    updateOrderFormErrorState({});
    updateOrderFormErrorPerSectionState({
      customerInformation: 0,
      loads: 0,
      instructionsAndCosts: 0,
    });

    // Block save if any TL/Partial load has out-of-order driver In/Out
    // chronology (only when the feature flag is enabled).
    const chronologyViolations = collectStopChronologyViolationsFor(
      orderStore.order.loads,
    );
    if (chronologyViolations.length > 0) {
      const first = chronologyViolations[0];
      const offendingLoad = orderStore.order.loads[first.loadIndex];
      const offendingStop = offendingLoad.stops?.[first.stopIndex];
      const stopLabel =
        offendingStop?.locationName || `Stop ${first.position + 1}`;
      const loadLabel =
        offendingLoad.loadNum ||
        `${orderStore.order.id}-${offendingLoad.sequence}`;
      handleToast(
        ToastType.Error,
        `${stopLabel} on Load ${loadLabel}: ${first.message}`,
      );
      return false;
    }

    const loads = orderStore.order.loads;

    if (
      orderStore.order.operationType === 'None' &&
      loads.every((load) => load.operationType === 'None') &&
      (orderStore.order.vendors?.every(
        (vendor) => vendor.operationType === 'None',
      ) ??
        true) &&
      orderStore.order.lineItems.every(
        (lineItem) => lineItem.operationType === 'None',
      ) &&
      loads.every((load) =>
        load.lineItems?.every((lineItem) => lineItem.operationType === 'None'),
      ) &&
      loads.every((load) =>
        load.stops?.every((stop) => stop.operationType === 'None'),
      ) &&
      loads.every((load) =>
        load.payments?.every((payment) => payment.operationType === 'None'),
      ) &&
      loads.every((load) =>
        load.items?.every((item) => item.operationType === 'None'),
      )
    ) {
      if (validateReferenceNumbers() === false) {
        handleToast(
          ToastType.Error,
          'No changes were detected. Please make changes to save the order.',
        );
      }

      return false;
    }

    return true;
  } catch (validationError: unknown) {
    // eslint-disable-next-line no-console
    console.log('validate error encountered', validationError);
    const errors: Record<string, string[]> = {};
    const mappedErrorSection: Record<string, string> = {};

    if (validationError instanceof ValidationError) {
      const errorsPerSection = {
        customerInformation: 0,
        loads: 0,
        vendor: 0,
        instructionsAndCosts: 0,
        carrier: 0,
        carrierDriverDetails: 0,
      };

      validationError.inner.forEach((error: ValidationError) => {
        if (error.path !== undefined) {
          errors[error.path] = error.errors;

          // @ts-expect-error `meta` is present on `spec`
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const section = error.params?.spec?.meta?.section as unknown as
            | keyof typeof errorsPerSection
            | undefined;

          if (section !== undefined) {
            errorsPerSection[section] += 1;
            mappedErrorSection[error.path] = section;
          }
        }
      });

      printLog('validationError', errors);
      // it will store mapped error state for each section to track which section has how many errors
      // if error in particular section is corrected then it will remove that section from error state
      updateOrderFormMappedErrorSection(mappedErrorSection);
      updateOrderFormErrorState(errors);
      updateOrderFormErrorPerSectionState(errorsPerSection);

      return false;
    }
  }
}

export function validateReferenceNumbers() {
  if (orderStore.order.loads.length === 0) {
    return false;
  }

  const loads = orderStore.order.loads;
  const hasLTL = loads.some((loads) => loads.mode === 'LTL');

  if (!hasLTL) {
    return false;
  }

  try {
    const referenceNumbers =
      ltlReferenceNumberStore.trackingData.referenceNumbers.filter(
        (referenceNumber) =>
          referenceNumber.type !== LTLReferenceNumberTypes.BOL &&
          referenceNumber.value.trim() !== '',
      );

    if (
      ltlReferenceNumberStore.operationType !== 'None' &&
      referenceNumbers.length > 0
    )
      return true;
  } catch (validationError: unknown) {
    return false;
  }
}
